import bugsnag from '@bugsnag/js'
import { format } from 'util'
import { getGlobalAxios } from '@odiupsk/up-api-client'
import { ErrorLogger, ErrorLoggerStateEnum } from '../common/modules/logger/ErrorLogger'
import { Auth } from '../common/modules/auth/Auth'
import { AuthCms } from '../common/modules/auth/AuthCms'
import { PersistentLogger } from '../common/modules/logger/PersistentLogger'
import { axiosParamsToSequence } from '../common/utils/axiosParamsToSequence'

/* eslint-disable no-param-reassign */

const CMS_API_BASE_URL = process.env.CMS_API_BASE_URL as string
const UP_API_BASE_URL = process.env.UP_API_BASE_URL as string
const API_TOKEN = process.env.API_TOKEN as string
const API_KEY = process.env.API_KEY as string
const API_KEY_PRODUCTION = process.env.API_KEY_PRODUCTION as string
const CLIENT_SECRET = process.env.CLIENT_SECRET as string
const CLIENT_SECRET_PRODUCTION = process.env.CLIENT_SECRET_PRODUCTION as string
const CLIENT_ID = process.env.CLIENT_ID as string
const RECAPTCHA_SITE_KEY = process.env.RECAPTCHA_SITE_KEY as string
const GOOGLE_ANALYTICS_KEY = process.env.GOOGLE_ANALYTICS_KEY as string
const BUGSNAG_KEY = process.env.BUGSNAG_KEY as string
const LAST_BUILD_DATE = process.env.LAST_BUILD_DATE as string

export {
  CMS_API_BASE_URL,
  UP_API_BASE_URL,
  API_TOKEN,
  CLIENT_ID,
  RECAPTCHA_SITE_KEY,
  GOOGLE_ANALYTICS_KEY,
  BUGSNAG_KEY,
  LAST_BUILD_DATE,
}

const DEV_HOSTNAMES = [
  'moje-preprod.up-slovensko.sk',
  'moje-dev.up-slovensko.sk',
  'moje-test.up-slovensko.sk',
  'moje-uat.up-slovensko.sk',
  'stravovanie-preprod.mojeup.sk',
  'stravovanie-uat.mojeup.sk',
  'localhost',
]

export const IS_DEMO = process.browser && DEV_HOSTNAMES.includes(location?.hostname)

const API_KEY_CONFIG: Record<string, string> = {
  'moje.up-slovensko.sk': API_KEY_PRODUCTION,
  'moje-preprod.up-slovensko.sk': API_KEY_PRODUCTION,
  'moje-dev.up-slovensko.sk': API_KEY,
  'moje-test.up-slovensko.sk': API_KEY,
  'moje-uat.up-slovensko.sk': API_KEY,
  'stravovanie.mojeup.sk': API_KEY_PRODUCTION,
  'stravovanie-preprod.mojeup.sk': API_KEY_PRODUCTION,
  'stravovanie-uat.mojeup.sk': API_KEY,
  localhost: API_KEY,
}

const CLIENT_SECRET_CONFIG: Record<string, string> = {
  'moje.up-slovensko.sk': CLIENT_SECRET_PRODUCTION,
  'moje-preprod.up-slovensko.sk': CLIENT_SECRET_PRODUCTION,
  'moje-dev.up-slovensko.sk': CLIENT_SECRET,
  'moje-test.up-slovensko.sk': CLIENT_SECRET,
  'moje-uat.up-slovensko.sk': CLIENT_SECRET,
  'stravovanie.mojeup.sk': CLIENT_SECRET_PRODUCTION,
  'stravovanie-preprod.mojeup.sk': CLIENT_SECRET_PRODUCTION,
  'stravovanie-uat.mojeup.sk': CLIENT_SECRET,
  localhost: CLIENT_SECRET,
}

const STATISTICS_URL_CONFIG: Record<string, string> = {
  'moje.up-slovensko.sk': 'https://gamification.up-dejeuner.sk',
  'moje-preprod.up-slovensko.sk': 'https://gamification.up-dejeuner.sk',
  'moje-dev.up-slovensko.sk': 'https://gamification-test.up-dejeuner.sk',
  'moje-test.up-slovensko.sk': 'https://gamification-test.up-dejeuner.sk',
  'moje-uat.up-slovensko.sk': 'https://gamification-test.up-dejeuner.sk',
  'stravovanie.mojeup.sk': 'https://gamification.up-dejeuner.sk',
  'stravovanie-preprod.mojeup.sk': 'https://gamification.up-dejeuner.sk',
  'stravovanie-uat.mojeup.sk': 'https://gamification-test.up-dejeuner.sk',
  localhost: 'https://gamification-test.up-dejeuner.sk',
}

export const CMS_CONFIG: Record<string, string> = {
  'moje.up-slovensko.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'moje-preprod.up-slovensko.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'moje-dev.up-slovensko.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'moje-test.up-slovensko.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'moje-uat.up-slovensko.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'stravovanie.mojeup.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'stravovanie-preprod.mojeup.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  'stravovanie-uat.mojeup.sk': 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
  localhost: 'BO95Keo0oG9aHk41qCB1unCSmT1VnUqZTKMwE0mc',
}

export const BUGSNAG_ENV: Record<string, string> = {
  'moje.up-slovensko.sk': 'production',
  'moje-preprod.up-slovensko.sk': 'pre-prod',
  'moje-dev.up-slovensko.sk': 'dev',
  'moje-test.up-slovensko.sk': 'test',
  'moje-uat.up-slovensko.sk': 'uat',
  'stravovanie.mojeup.sk': 'production',
  'stravovanie-preprod.mojeup.sk': 'pre-prod',
  'stravovanie-uat.mojeup.sk': 'uat',
  localhost: 'localhost',
}

export const statisticsDomain = process.browser
  ? STATISTICS_URL_CONFIG[location.hostname] || STATISTICS_URL_CONFIG.localhost
  : STATISTICS_URL_CONFIG.localhost

export const statisticsOverviewPath = 'statistika'
export const statisticsGamesPath = 'sutaze'

const apiKey = process.browser
  ? API_KEY_CONFIG[location.hostname] || API_KEY_CONFIG.localhost
  : API_KEY_CONFIG.localhost

const clientSecret = process.browser
  ? CLIENT_SECRET_CONFIG[location.hostname] || CLIENT_SECRET_CONFIG.localhost
  : CLIENT_SECRET_CONFIG.localhost

const bugsnagEnv = process.browser
  ? BUGSNAG_ENV[location.hostname] || BUGSNAG_ENV.localhost
  : BUGSNAG_ENV.localhost

export { clientSecret, apiKey, bugsnagEnv }

//
// Bugsnag SETUP
//

// eslint-disable-next-line import/no-mutable-exports
let bugsnagClient: any

const createMetadata = (prefix: string, data: object) => {
  const metadata: { [key: string]: string } = {}

  if (data) {
    Object.entries(data).forEach(([key, value]) => {
      metadata[`${prefix ? `(${prefix}) ` : ''}${key}`] = value
    })
  }

  return metadata
}

if (process.env.NODE_ENV !== 'test') {
  bugsnagClient = bugsnag({
    apiKey: BUGSNAG_KEY,
    appVersion: LAST_BUILD_DATE,
    autoNotify: true /* bugsnagEnv === 'production' - TODO */,
    releaseStage: bugsnagEnv,
    beforeSend: report => {
      try {
        //
        // bugsnag report metadata
        //
        const {
          // app,
          // apiKey,
          breadcrumbs,
          context,
          // device,
          // errorClass,
          errorMessage,
          // groupingHash,
          // metaData,
          severity, // "info" | "warning" | "error"
          // stacktrace,
          // user,
          // session,
          // request, // { url: string }
          originalError,
        } = report

        const bugsnagReportMetadata = createMetadata('', report)
        // delete bugsnagReportMetadata.breadcrumbs

        //
        // error setup
        //
        const messages = originalError.response?.data?.messages || []
        const message = messages?.[0]?.message || errorMessage

        const statusCode = originalError.statusCode || originalError.response?.status

        //
        // handle context and non-context based errors
        //
        const nameSeparator = ' ... '

        let name = `${severity ? `${severity.toUpperCase()}` : ``}${
          statusCode ? ` ${statusCode}` : ''
        }`

        const errorsWithContext = [404]
        const errorsWithoutContext = [524]

        const isHandledWithoutContext =
          statusCode &&
          (errorsWithoutContext.includes(statusCode) ||
            (statusCode >= 400 && statusCode < 500 && !errorsWithContext.includes(statusCode)))

        // Errors tied to specific url
        if (isHandledWithoutContext) {
          if (message) {
            name += `${nameSeparator}${message}`
          }
          if (statusCode === 524) {
            name += `${nameSeparator}Cloudflare-specific timeout (server did not respond in time)`
          }
        }

        // Global errors (auth etc.)
        else if (context) {
          name += `${nameSeparator}${context}`
        }

        // Network Error (override name)
        const networkError = 'Network Error'
        let networkErrorDetected = message === networkError

        if (!networkErrorDetected) {
          breadcrumbs?.forEach(breadcrumb => {
            if ((breadcrumb?.metaData as any)?.errorClass === networkError) {
              networkErrorDetected = true
            }
          })
        }

        if (message === 'Network Error') {
          name = message.toUpperCase()
        }

        const errorClass = name
        const groupingHash = name.replace(/\s/g, '-')

        //
        // FLOOD CONTROL
        //
        const errorLoggerData = ErrorLogger.addLog(errorClass)
        const errorData = errorLoggerData[errorClass]
        let ignoreBug = false

        // console.log(errorData.state, errorData.logs.length)

        switch (errorData.state) {
          case ErrorLoggerStateEnum.freezeStart:
            report.errorClass = `!! FREEZE START !! ${errorClass}`
            report.groupingHash = `freeze-start-${groupingHash}`
            break
          // case ErrorLoggerStateEnum.freezeEnd:
          // report.errorClass = `FREEZE END - ${errorClass}` // TODO - IS THIS WANTED? FREEZE-END IS A REGULAR ERROR AFTER A LONG ENOUGH TIME PASSED
          // report.groupingHash = `freeze-end-${groupingHash}`
          // break
          case ErrorLoggerStateEnum.freezed:
            ignoreBug = true
            break
          default:
            report.errorClass = errorClass
            report.groupingHash = groupingHash
        }

        if (ignoreBug) {
          report.ignore()
          return
        }

        //
        // pass data to bugsnag
        //
        report.updateMetaData('Report', bugsnagReportMetadata)
        report.updateMetaData('Access Token', Auth.getAccessTokenData())
        report.updateMetaData('Persistent Logger', PersistentLogger.getLogs())
        report.updateMetaData('Error Logger (Flood Protection)', {
          thisError: errorData,
          allErrors: errorLoggerData,
        })
        report.updateMetaData('Metadata', {
          version: {
            buildDate: LAST_BUILD_DATE,
          },
          config: {
            cmsApiBaseUrl: CMS_API_BASE_URL,
            upApiBaseUrl: UP_API_BASE_URL,
          },
          error: {
            statusCode,
            errorMessage: message,
          },
          requestUrl: report.request?.url,
        })
      } catch (e) {
        report.updateMetaData('Bugsnag Setup Error', {
          version: {
            buildDate: LAST_BUILD_DATE,
          },
          bugsnagError: {
            e,
          },
        })
      }
    },
  })
} else {
  bugsnagClient = {
    notify: () => ({}),
  }
}

export { bugsnagClient }

export const cmsAxios = getGlobalAxios().create({
  baseURL: CMS_API_BASE_URL,
})

cmsAxios.interceptors.request.use(req => {
  const accessToken = AuthCms.getAccessToken()
  if (accessToken) req.headers.Authorization = `Bearer ${accessToken}`
  return req
})

export const upAxios = getGlobalAxios().create({
  baseURL: UP_API_BASE_URL,
  headers: {
    'X-API-KEY': apiKey,
    'X-Language': `1`,
    'X-Frame-Options': 'SAMEORIGIN',
  },
  paramsSerializer: (data: any) => {
    const params = new URLSearchParams(axiosParamsToSequence(data)).toString()
    return params
  },
})

const omitAuthRedirectUrls: string[] = [
  '/client/order',
  '/client/order/result',
  '/product-selection',
]

const onlyToAuthRedirectUrls: string[] = ['/auth/change-email']

export const shouldOmitRedirect = () =>
  omitAuthRedirectUrls.includes(location.pathname.replace(/\/$/, ''))

export const shouldRedirectOnlyToAuth = () =>
  onlyToAuthRedirectUrls.includes(location.pathname.replace(/\/$/, ''))

const showLivechatUrls: string[] = [
  '/product-selection',
  '/client/product-selection',
  '/client/order',
  '/client/order/result',
  '/auth',
]

export const shouldShowLivechat = () =>
  showLivechatUrls.includes(location.pathname.replace(/\/$/, ''))

if (process.env.NODE_ENV === 'test') {
  ;['request', 'get', 'post', 'patch', 'put', 'delete'].forEach(i => {
    const mock = jest.fn((...args) => {
      throw new Error(`Outgoing requests in TEST are forbidden ${format(args)}`)
    })
    ;(upAxios as any)[i] = mock
    ;(cmsAxios as any)[i] = mock
  })
}

upAxios.interceptors.request.use(req => {
  const accessToken = Auth.getAccessToken()
  if (accessToken) req.headers.Authorization = `Bearer ${accessToken}`

  if (JSON.stringify(req.params || '').length > 2000 && req.method === 'get') {
    req.method = 'post'
    req.data = req.params
    req.params = undefined
  }

  return req
})

upAxios.interceptors.response.use(
  response => {
    return response
  },
  async err => {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'test') console.log(err)

    if (
      err.response?.status === 401 &&
      err.config &&
      !upAxios.defaults.headers.common.isRetryRequest
    ) {
      const origin = 'src/config/index.ts'

      try {
        upAxios.defaults.headers.common.isRetryRequest = 1

        await Auth.fetchNewAccessToken({ origin })

        const accessToken = Auth.getAccessToken()
        err.config.headers.Authorization = `Bearer ${accessToken}`

        // reset baseURL otherwise it would be prepended to url again
        err.config.baseURL = ''

        upAxios.defaults.headers.common.isRetryRequest = 0
        return upAxios(err.config)
      } catch (error) {
        upAxios.defaults.headers.common.isRetryRequest = 0

        Auth.removeAccessToken()

        const accessToken = Auth.getAccessToken() // to check if token has been removed

        PersistentLogger.log({
          message: `ERROR RE-FETCHING TOKEN ON 401`,
          origin,
          debugData: {
            storedToken: accessToken?.length ? accessToken : 'no token',
            '0_cause':
              'Interceptor caught 401 response, tried to fetch a new token but that also failed',
            '1_action':
              'Interceptor removed access token from storage and will now redirect (based on redirect logic)',
          },
        })

        if (shouldOmitRedirect()) {
          return
        }

        if (shouldRedirectOnlyToAuth()) {
          location.href = `/auth`
          return
        }

        location.href = `/auth?redirect=${encodeURIComponent(location.pathname)}`
      }
    }

    throw err
  }
)

cmsAxios.interceptors.response.use(
  response => {
    return response
  },
  async err => {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'test') console.log(err)

    if (
      err.response.status === 401 &&
      err.config &&
      !cmsAxios.defaults.headers.common.isRetryRequest
    ) {
      try {
        cmsAxios.defaults.headers.common.isRetryRequest = 1
        await AuthCms.login()

        const accessToken = AuthCms.getAccessToken()
        err.config.headers.Authorization = `Bearer ${accessToken}`
        // reset baseURL otherwise it would be prepended to url again
        err.config.baseURL = ''
        cmsAxios.defaults.headers.common.isRetryRequest = 0

        return cmsAxios(err.config)
      } catch (error) {
        cmsAxios.defaults.headers.common.isRetryRequest = 0
      }
    }

    throw err
  }
)
